<template>
  <div class="chart-container">
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const { locale } = useI18n();

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  range: {
    type: String,
    default: '1d'
  }
});

const chartData = computed(() => {
  // Determina se tutti i dati sono nello stesso giorno (granularità oraria)
  const dates = props.data.map(d => new Date(d._id));
  const allSameDay = dates.length > 0 && dates.every(d => {
    const first = dates[0];
    return d.getFullYear() === first.getFullYear() && 
           d.getMonth() === first.getMonth() && 
           d.getDate() === first.getDate();
  });

  const currentLocale = locale.value === 'it' ? 'it-IT' : 'en-US';
  
  return {
    labels: props.data.map(d => {
      const date = new Date(d._id);
      if (isNaN(date.getTime())) {
        return '';
      }
      
      // Se tutti i dati sono dello stesso giorno, mostra ore:minuti
      if (allSameDay) {
        return date.toLocaleTimeString(currentLocale, { hour: '2-digit', minute: '2-digit' });
      }
      
      // Altrimenti mostra giorno e mese
      return date.toLocaleDateString(currentLocale, { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Tempo di Risposta (ms)',
        data: props.data.map(d => d.avgDuration || 0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 10,
      titleColor: '#fff',
      bodyColor: '#fff',
      callbacks: {
        label: function(context) {
          const value = context.parsed.y;
          if (value < 1000) {
            return `${value.toFixed(0)} ms`;
          }
          return `${(value / 1000).toFixed(2)} s`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        color: '#9ca3af',
        callback: function(value) {
          if (value < 1000) {
            return value + ' ms';
          }
          return (value / 1000).toFixed(1) + ' s';
        }
      },
      grid: {
        color: 'rgba(156, 163, 175, 0.1)'
      }
    },
    x: {
      ticks: {
        color: '#9ca3af'
      },
      grid: {
        display: false
      }
    }
  }
};
</script>

<style scoped>
.chart-container {
  height: 200px;
  width: 100%;
}
</style>
